/* import __COLOCATED_TEMPLATE__ from './conversation-options.hbs'; */
/* RESPONSIBLE TEAM: team-inbox-experience */
import Component from '@glimmer/component';
import type Conversation from 'embercom/objects/inbox/conversation';
import { NewConversation } from 'embercom/objects/inbox/conversation';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import type IntlService from 'embercom/services/intl';
import type InboxState from 'embercom/services/inbox-state';
import { HotkeyID } from 'embercom/services/inbox-hotkeys/HotkeyID';
import type Session from 'embercom/services/session';
import type Router from '@ember/routing/router-service';
import {
  type ReplyChannel,
  replyChannelIsMultiParticipant,
  ReplyChannelMap,
} from 'embercom/objects/inbox/composer-pane';
import { tracked } from '@glimmer/tracking';
import type CommandKService from 'embercom/services/command-k';
import { Channel } from 'embercom/models/data/inbox/channels';
import type ConversationTranslationSettings from 'embercom/services/conversation-translation-settings';
import type TeammateAiTranslationSettings from 'embercom/services/teammate-ai-translation-settings';
import type CopilotUsageService from 'embercom/services/copilot-usage-service';
import { TRANSLATIONS_FREE_CREDIT_LIMIT } from 'embercom/services/copilot-usage-service';
import { canExportConversationTranscriptsInbox2 } from 'embercom/lib/settings/permissions/inline-permission-enforcement';
import type PermissionsModal from 'embercom/services/permissions-modal';
import type ConversationStreamSettings from 'embercom/services/conversation-stream-settings';

interface Args {
  conversation: Conversation | NewConversation;
  hideConversationEvents: boolean;
  onToggleConversationEvents: () => unknown;
  exportConversation: () => void;
  exportConversationPdf?: () => void;
  allowManageParticipants?: boolean;
  markAsSpam?: () => unknown;
}

interface Signature {
  Args: Args;
}

enum DropdownOption {
  newConversation = 'new-conversation',
  conversationEvents = 'conversation-events',
  exportConversationText = 'export-conversation-text',
  exportConversationPdf = 'export-conversation-pdf',
  manageParticipants = 'manage-participants',
  mergeInto = 'merge-into',
  translateConversation = 'translate-conversation',
  markAsSpam = 'mark-as-spam',
  customerTimezone = 'customer-timezone',
}

type MoreOptionItem = {
  text?: string;
  badge?: { klass: string; args?: Record<string, unknown> };
  id: DropdownOption | string;
  isSelected: boolean;
  isDisabled: boolean;
  icon?: string;
  iconSet?: string;
  hotkeyId?: HotkeyID;
  tooltipText?: string;
  tooltipContent?: { klass: string; args?: Record<string, unknown> };
};

const CROSS_USER_MERGEABLE_CHANNELS = [Channel.Email, Channel.PhoneCall];

abstract class ConversationOption {
  conversationOrTicket: Conversation;
  session: Session;
  intl: IntlService;

  protected constructor(conversationOrTicket: Conversation, session: Session, intl: IntlService) {
    this.conversationOrTicket = conversationOrTicket;
    this.session = session;
    this.intl = intl;
  }

  get isTicket() {
    return this.conversationOrTicket.isTicket;
  }

  get isUnreplyableTicket() {
    return this.isTicket && !this.conversationOrTicket.isCustomerTicket;
  }

  abstract get id(): DropdownOption;
  abstract get icon(): string;

  get text(): string | undefined {
    return undefined;
  }

  get badge(): { klass: string; args?: Record<string, unknown> } | undefined {
    return undefined;
  }

  get iconSet(): string | undefined {
    return undefined;
  }

  get hotkeyId(): HotkeyID | undefined {
    return undefined;
  }

  get tooltipText(): string | undefined {
    return undefined;
  }

  get tooltipContent(): { klass: string; args?: Record<string, unknown> } | undefined {
    return undefined;
  }

  get isSelected(): boolean {
    return false;
  }

  get isDisabled(): boolean {
    return false;
  }

  get isVisible(): boolean {
    return true;
  }

  get isVisibleOnCmdK(): boolean {
    return this.isVisible;
  }

  protected get isVisibleInCurrentInbox(): boolean {
    if (this.session.workspace.isStandaloneApp) {
      return true;
    }
    return !this.session.showLightInbox;
  }

  toOptionItem(): MoreOptionItem {
    return {
      id: this.id,
      text: this.text,
      badge: this.badge,
      isSelected: this.isSelected,
      isDisabled: this.isDisabled,
      icon: this.icon,
      iconSet: this.iconSet,
      hotkeyId: this.hotkeyId,
      tooltipText: this.tooltipText,
      tooltipContent: this.tooltipContent,
    };
  }
}

class ManageParticipantsOption extends ConversationOption {
  allowManageParticipants: boolean | undefined;
  replyChannelIsMultiParticipant: (replyChannel?: ReplyChannel) => boolean;

  constructor(
    conversationOrTicket: Conversation,
    session: Session,
    intl: IntlService,
    allowManageParticipants: boolean | undefined,
    replyChannelIsMultiParticipant: (replyChannel?: ReplyChannel) => boolean,
  ) {
    super(conversationOrTicket, session, intl);
    this.allowManageParticipants = allowManageParticipants;
    this.replyChannelIsMultiParticipant = replyChannelIsMultiParticipant;
  }

  get id() {
    return DropdownOption.manageParticipants;
  }

  get text() {
    return this.intl.t('inbox.conversation-header.manage-participants');
  }

  get icon() {
    return 'multiple-people';
  }

  get iconSet() {
    return 'standard';
  }

  get hasPermission() {
    return this.session.teammate.permissions.canManageConversationParticipants;
  }

  get isVisible() {
    if (
      this.session.showLightInbox ||
      this.session.shouldShowExternalConversationMode(this.conversationOrTicket)
    ) {
      return false;
    }

    if (this.isUnreplyableTicket) {
      return false;
    }

    let replyChannel = ReplyChannelMap[this.conversationOrTicket.channel.replyChannel];

    return (
      this.allowManageParticipants === true &&
      this.replyChannelIsMultiParticipant(replyChannel) &&
      this.conversationOrTicket.channel.current !== Channel.Slack &&
      this.conversationOrTicket.channel.current !== Channel.Discord
    );
  }
}

class NewConversationOption extends ConversationOption {
  constructor(conversationOrTicket: Conversation, session: Session, intl: IntlService) {
    super(conversationOrTicket, session, intl);
  }

  get id() {
    return DropdownOption.newConversation;
  }

  get text() {
    return this.intl.t('inbox.conversation-header.new-conversation');
  }

  get icon() {
    return 'compose';
  }

  get iconSet() {
    return 'standard';
  }

  get isVisible() {
    if (this.session.showLightInbox) {
      return false;
    }

    if (this.isUnreplyableTicket) {
      return false;
    }

    return true;
  }
}

class MergeIntoOption extends ConversationOption {
  constructor(conversationOrTicket: Conversation, session: Session, intl: IntlService) {
    super(conversationOrTicket, session, intl);
  }

  get id() {
    return DropdownOption.mergeInto;
  }

  get text() {
    return this.intl.t('inbox.conversation-header.merge-into');
  }

  get icon() {
    return 'merge';
  }

  get hotkeyId() {
    return HotkeyID.MergeInto;
  }

  get hasPermission() {
    return this.session.teammate.permissions.canMergeConversations;
  }

  get tooltipText() {
    if (this.hasMultipleParticipants && !this.isCrossUserMergeAllowed) {
      let translationKey = this.isTicket
        ? 'inbox.conversation-header.merge-into-disabled.ticket-multiple-participants'
        : 'inbox.conversation-header.merge-into-disabled.conversation-multiple-participants';

      return this.intl.t(translationKey);
    }

    if (this.isUnreplyableTicket) {
      return this.intl.t('inbox.conversation-header.merge-into-disabled.ticket-not-customer');
    }

    return undefined;
  }

  get isDisabled() {
    if (this.hasMultipleParticipants && !this.isCrossUserMergeAllowed) {
      return true;
    }

    if (this.isUnreplyableTicket) {
      return true;
    }

    return false;
  }

  get isVisible() {
    if (
      this.session.showLightInbox ||
      this.session.shouldShowExternalConversationMode(this.conversationOrTicket)
    ) {
      return false;
    }

    return true;
  }

  get isVisibleOnCmdK() {
    if (this.isUnreplyableTicket) {
      return false;
    }

    if (!this.isCrossUserMergeAllowed && this.hasMultipleParticipants) {
      return false;
    }

    return true;
  }

  get hasMultipleParticipants() {
    return this.conversationOrTicket.participantSummaries.length > 1;
  }

  get isCrossUserMergeAllowed(): boolean {
    return (
      this.session.workspace.enabledCrossUserConversationMerge &&
      CROSS_USER_MERGEABLE_CHANNELS.includes(this.conversationOrTicket?.channel?.initial)
    );
  }
}

class ExportConversationTextOption extends ConversationOption {
  constructor(conversationOrTicket: Conversation, session: Session, intl: IntlService) {
    super(conversationOrTicket, session, intl);
  }

  get id() {
    return DropdownOption.exportConversationText;
  }

  get text() {
    return this.isTicket
      ? this.intl.t('inbox.conversation-header.export-ticket-text')
      : this.intl.t('inbox.conversation-header.export-conversation-text');
  }

  get icon() {
    return 'export';
  }

  get isVisible() {
    return this.isVisibleInCurrentInbox;
  }
}

class ExportConversationPdfOption extends ConversationOption {
  constructor(conversationOrTicket: Conversation, session: Session, intl: IntlService) {
    super(conversationOrTicket, session, intl);
  }

  get id() {
    return DropdownOption.exportConversationPdf;
  }

  get text() {
    return this.isTicket
      ? this.intl.t('inbox.conversation-header.export-ticket-pdf')
      : this.intl.t('inbox.conversation-header.export-conversation-pdf');
  }

  get icon() {
    return 'export';
  }

  get isVisible() {
    if (!canExportConversationTranscriptsInbox2(this.session)) {
      return false;
    }

    return this.isVisibleInCurrentInbox;
  }
}

class ShowEventsOption extends ConversationOption {
  hideEvents: boolean;

  constructor(
    conversationOrTicket: Conversation,
    session: Session,
    intl: IntlService,
    hideEvents: boolean,
  ) {
    super(conversationOrTicket, session, intl);

    this.hideEvents = hideEvents;
  }

  get id() {
    return DropdownOption.conversationEvents;
  }

  get text() {
    return this.isTicket
      ? this.intl.t('inbox.conversation-header.ticket-events')
      : this.intl.t('inbox.conversation-header.conversation-events');
  }

  get icon() {
    return 'visible';
  }

  get hotkeyId() {
    return HotkeyID.ToggleEvents;
  }

  get isSelected() {
    return !this.hideEvents;
  }
}

class ShowCustomerTimezoneOption extends ConversationOption {
  showCustomerTimezone: boolean;

  constructor(
    conversationOrTicket: Conversation,
    session: Session,
    intl: IntlService,
    showCustomerTimezone: boolean,
  ) {
    super(conversationOrTicket, session, intl);
    this.showCustomerTimezone = showCustomerTimezone;
  }

  get id() {
    return DropdownOption.customerTimezone;
  }

  get text() {
    return this.intl.t('inbox.conversation-header.customer-timezone');
  }

  get icon() {
    return 'clock';
  }

  get isSelected() {
    return this.showCustomerTimezone;
  }

  get isVisible() {
    return this.session.workspace.isFeatureEnabled('spg-inbox-customer-timezone');
  }
}

class TranslateConversationOption extends ConversationOption {
  conversationTranslationSettings: ConversationTranslationSettings;
  teammateAiTranslationSettings: TeammateAiTranslationSettings;
  hasTranslationsLeft: boolean;
  copilotUsageService: CopilotUsageService;

  constructor(
    conversationOrTicket: Conversation,
    session: Session,
    intl: IntlService,
    conversationTranslationSettings: ConversationTranslationSettings,
    teammateAiTranslationSettings: TeammateAiTranslationSettings,
    copilotUsageService: CopilotUsageService,
  ) {
    super(conversationOrTicket, session, intl);

    this.conversationTranslationSettings = conversationTranslationSettings;
    this.teammateAiTranslationSettings = teammateAiTranslationSettings;
    this.copilotUsageService = copilotUsageService;
    this.hasTranslationsLeft = this.copilotUsageService.hasTranslationsLeft(
      this.session.teammate,
      this.conversationOrTicket,
    );
  }

  get id() {
    return DropdownOption.translateConversation;
  }

  get icon() {
    return 'language';
  }

  get text() {
    return this.intl.t('inbox.conversation-header.translations.translate-conversation');
  }

  get badge() {
    if (this.session.workspace.isStandaloneApp) {
      return undefined;
    }

    return {
      klass: 'inbox2/translate-conversation-dropdown',
      args: {
        hideLimitIndicator: this.session.teammate.hasUnlimitedCopilotAccess,
        hasTranslationsLeft: this.hasTranslationsLeft,
        quota: {
          used: this.copilotUsageService.getAdminUsedTranslationsCountFromConversation(
            this.conversationOrTicket,
          ),
          available: TRANSLATIONS_FREE_CREDIT_LIMIT,
        },
      },
    };
  }

  get conversationLanguage(): string {
    let conversationLanguage =
      this.conversationOrTicket.getAttributeForDescriptor('language')?.value;
    if (typeof conversationLanguage !== 'string') {
      return this.session.workspace.locale || '';
    }
    return conversationLanguage;
  }

  get conversationLanguageIsExcluded() {
    return this.teammateAiTranslationSettings.excludedLanguages.includes(this.conversationLanguage);
  }

  get isSelected() {
    return this.conversationTranslationSettings.autoTranslationEnabledForConversationObject(
      this.conversationOrTicket,
    );
  }

  get isVisible() {
    if (!this.conversationTranslationSettings.autoTranslationEnabled) {
      return false;
    }
    if (!this.conversationLanguage) {
      return false;
    }

    let workspaceDefaultLanguage = this.session.workspace.locale;
    let isConversationLanguageSameAsWorkspaceDefaultLanguage =
      this.conversationLanguage === workspaceDefaultLanguage;
    if (isConversationLanguageSameAsWorkspaceDefaultLanguage) {
      return false;
    }

    if (!this.session.teammate.hasCopilotAccess) {
      return false;
    }

    return true;
  }

  get isDisabled() {
    if (this.conversationLanguageIsExcluded) {
      return true;
    }

    return !this.conversationTranslationSettings.hasTranslationAccessForConversation(
      this.conversationOrTicket,
    );
  }

  get tooltipContent() {
    if (this.session.workspace.isStandaloneApp) {
      return undefined;
    }

    if (this.conversationLanguageIsExcluded) {
      return {
        klass: 'inbox2/translate-conversation-skip-languages-tooltip',
        args: {
          conversationLanguage: this.conversationLanguage,
        },
      };
    }

    let hasUnlimitedCopilotAccess = this.session.teammate.hasUnlimitedCopilotAccess;
    if (hasUnlimitedCopilotAccess) {
      return undefined;
    }

    return {
      klass: 'inbox2/translate-conversation-tooltip',
      args: {
        isLimitReached: !this.hasTranslationsLeft,
        quota: {
          used: this.copilotUsageService.getAdminUsedTranslationsCountFromConversation(
            this.conversationOrTicket,
          ),
          available: TRANSLATIONS_FREE_CREDIT_LIMIT,
        },
      },
    };
  }
}

class MarkAsSpamOption extends ConversationOption {
  constructor(conversationOrTicket: Conversation, session: Session, intl: IntlService) {
    super(conversationOrTicket, session, intl);
  }

  get id() {
    return DropdownOption.markAsSpam;
  }

  get text() {
    return this.intl.t('inbox.conversation-header.mark-as-spam');
  }

  get icon() {
    return 'spam';
  }

  get isVisible() {
    if (
      this.session.showLightInbox ||
      this.session.shouldShowExternalConversationMode(this.conversationOrTicket)
    ) {
      return false;
    }

    return (
      !this.isTicket &&
      !this.conversationOrTicket.markedAsSpam &&
      this.conversationOrTicket.initialChannel === Channel.Email &&
      this.session.workspace.isFeatureEnabled('fin-service-agent-spam-view')
    );
  }
}

export default class Inbox2ConversationOptionDropdownComponent extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare intercomEventService: any;
  @service declare inboxState: InboxState;
  @service declare session: Session;
  @service declare router: Router;
  @service declare commandK: CommandKService;
  @service declare conversationTranslationSettings: ConversationTranslationSettings;
  @service declare teammateAiTranslationSettings: TeammateAiTranslationSettings;
  @service declare copilotUsageService: CopilotUsageService;
  @service declare permissionsModal: PermissionsModal;
  @service declare conversationStreamSettings: ConversationStreamSettings;

  @tracked isManagingParticipants = false;

  get isNewConversation() {
    return this.args.conversation instanceof NewConversation;
  }
  get moreOptionItems(): MoreOptionItem[] {
    if (this.isNewConversation) {
      return [];
    }

    let options: ConversationOption[] = [
      this.manageParticipantsOption,
      new MergeIntoOption(this.conversation, this.session, this.intl),
      new NewConversationOption(this.conversation, this.session, this.intl),
      new MarkAsSpamOption(this.conversation, this.session, this.intl),
      new ExportConversationTextOption(this.conversation, this.session, this.intl),
      new ExportConversationPdfOption(this.conversation, this.session, this.intl),
      new ShowEventsOption(
        this.conversation,
        this.session,
        this.intl,
        this.args.hideConversationEvents,
      ),
      new ShowCustomerTimezoneOption(
        this.conversation,
        this.session,
        this.intl,
        this.conversationStreamSettings.showCustomerTimezone,
      ),
      new TranslateConversationOption(
        this.conversation,
        this.session,
        this.intl,
        this.conversationTranslationSettings,
        this.teammateAiTranslationSettings,
        this.copilotUsageService,
      ),
    ];

    return options.filter((option) => option.isVisible).map((option) => option.toOptionItem());
  }

  get conversation(): Conversation {
    return this.args.conversation as Conversation;
  }

  get manageParticipantsOption() {
    return new ManageParticipantsOption(
      this.conversation,
      this.session,
      this.intl,
      this.args.allowManageParticipants,
      replyChannelIsMultiParticipant,
    );
  }

  get mergeIntoOption() {
    return new MergeIntoOption(this.conversation, this.session, this.intl);
  }

  get showManagingParticipantsCmdKAction() {
    if (this.isNewConversation) {
      return false;
    }

    return this.manageParticipantsOption.isVisibleOnCmdK;
  }

  get showMergeIntoCmdKAction() {
    if (this.isNewConversation) {
      return false;
    }

    return this.mergeIntoOption.isVisibleOnCmdK;
  }

  @action showManageParticipantsModal() {
    if (!this.manageParticipantsOption.hasPermission) {
      this.permissionsModal.showModal({
        permissionsLabel: this.intl.t(
          'inbox.conversation-header.manage-participants-permission-label',
        ),
      });
      return;
    }

    this.isManagingParticipants = true;
  }

  @action showMergeIntoModal() {
    if (!this.mergeIntoOption.hasPermission) {
      this.permissionsModal.showModal({
        permissionsLabel: this.intl.t(
          'inbox.conversation-header.merge-conversations-permission-label',
        ),
      });
      return;
    }

    this.commandK.findAndShow('merge-into', () => {});
  }

  @action startNewConversation() {
    if (!this.session.teammate.permissions.canManageConversationCreation) {
      this.permissionsModal.showModal({
        permissionsLabel: this.intl.t(
          'inbox.conversation-header.create-conversation-permission-label',
        ),
      });
      return;
    }

    this.router.transitionTo('inbox.workspace.inbox.new-conversation', {
      queryParams: {
        recipient: this.conversation.userSummary.id,
        ticketTypeId: undefined,
      },
    });
  }

  @action onMoreOptionsSelectionChange(item: MoreOptionItem) {
    switch (item.id) {
      case DropdownOption.newConversation:
        this.startNewConversation();
        break;
      case DropdownOption.conversationEvents:
        this.args.onToggleConversationEvents();
        this.intercomEventService.trackAnalyticsEvent({
          object: 'visibility',
          action: 'changed',
          section: 'conversation_header',
          conversation_id: this.conversation.id,
          visibility_type: 'events',
          is_visible: !this.args.hideConversationEvents,
        });
        break;
      case DropdownOption.mergeInto: {
        this.showMergeIntoModal();

        if (this.mergeIntoOption.hasPermission) {
          this.intercomEventService.trackAnalyticsEvent({
            object: 'merge_into',
            action: 'clicked',
            section: 'conversation_header',
            conversation_id: this.conversation.id,
            using_modal: true,
          });
        }
        break;
      }
      case DropdownOption.exportConversationText:
        this.args.exportConversation();
        break;
      case DropdownOption.exportConversationPdf:
        if (this.args.exportConversationPdf) {
          this.args.exportConversationPdf();
        }
        break;
      case DropdownOption.translateConversation: {
        this.conversationTranslationSettings.toggleAutoTranslationEnabledForConversation(
          this.conversation.id,
        );
        break;
      }
      case DropdownOption.manageParticipants: {
        this.showManageParticipantsModal();

        if (this.manageParticipantsOption.hasPermission) {
          this.intercomEventService.trackAnalyticsEvent({
            object: 'manage_participants',
            action: 'clicked',
            section: 'conversation_header',
            conversation_id: this.conversation.id,
            using_modal: true,
          });
        }
        break;
      }
      case DropdownOption.markAsSpam:
        if (this.args.markAsSpam) {
          this.args.markAsSpam();
        }
        break;
      case DropdownOption.customerTimezone:
        this.conversationStreamSettings.toggleCustomerTimezone();
        this.intercomEventService.trackAnalyticsEvent({
          object: 'visibility',
          action: 'changed',
          section: 'conversation_header',
          conversation_id: this.conversation.id,
          visibility_type: 'customer_timezone',
          is_visible: this.conversationStreamSettings.showCustomerTimezone,
        });
        break;
      default:
        break;
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::ConversationOptions': typeof Inbox2ConversationOptionDropdownComponent;
    'inbox2/conversation-options': typeof Inbox2ConversationOptionDropdownComponent;
  }
}
